import { useState } from "react";
import { getAuthorization } from "@/Utils/authSession";

const BASE_URL = process.env.NODE_API_URL;

const codeOf = (body: unknown): string | null =>
  body &&
  typeof body === "object" &&
  "code" in body &&
  typeof body.code === "string" &&
  body.code
    ? body.code
    : null;

export class ApiRequestError extends Error {
  status: number;
  body: unknown;
  retryAfter: number | null;
  code: string | null;

  constructor(
    message: string,
    status: number,
    body?: unknown,
    retryAfter: number | null = null
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
    this.retryAfter = retryAfter;
    this.code = codeOf(body);
  }
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : "Ocorreu um erro ao processar a solicitação.";

export interface RequestConfig {
  platform?: boolean;
  public?: boolean;
}

const useApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequest = async (
    endpoint: string,
    method: string,
    data?: object,
    headers?: Record<string, string>,
    config?: RequestConfig
  ): Promise<Response> => {
    try {
      setIsLoading(true);
      setError(null);

      const isPublic = config?.public === true;
      const companyId = localStorage.getItem("companyId");
      const authorization = isPublic ? undefined : getAuthorization();

      const isFormData = data instanceof FormData;

      const options: RequestInit = {
        method,
        headers: {
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          ...(authorization ? { Authorization: authorization } : {}),
          ...(companyId && !config?.platform && !isPublic
            ? { "X-Company-Id": companyId }
            : {}),
          ...headers,
        },
      };

      if (data) {
        options.body = data instanceof FormData ? data : JSON.stringify(data);
      }

      const response = await fetch(`${BASE_URL}${endpoint}`, options);

      if (!response.ok) {
        const rawBody = await response.text();
        let parsedBody: unknown = rawBody;

        try {
          parsedBody = rawBody ? JSON.parse(rawBody) : null;
        } catch {
          parsedBody = rawBody;
        }

        const errorMessage =
          parsedBody &&
          typeof parsedBody === "object" &&
          "message" in parsedBody &&
          typeof parsedBody.message === "string"
            ? parsedBody.message
            : rawBody || response.statusText;
        const retryAfterHeader = response.headers?.get?.("Retry-After");
        const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : NaN;
        throw new ApiRequestError(
          errorMessage,
          response.status,
          parsedBody && typeof parsedBody === "object" ? parsedBody : undefined,
          Number.isFinite(retryAfter) ? retryAfter : null
        );
      }

      return response;
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError));
      throw requestError;
    } finally {
      setIsLoading(false);
    }
  };

  const getRequest = async (
    endpoint: string,
    headers?: Record<string, string>,
    config?: RequestConfig
  ): Promise<Response> => {
    return handleRequest(endpoint, "GET", undefined, headers, config);
  };

  const postRequest = async (
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    config?: RequestConfig
  ): Promise<Response> => {
    return handleRequest(endpoint, "POST", data, headers, config);
  };

  const putRequest = async (
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    config?: RequestConfig
  ): Promise<Response> => {
    return handleRequest(endpoint, "PUT", data, headers, config);
  };

  const patchRequest = async (
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    config?: RequestConfig
  ): Promise<Response> => {
    return handleRequest(endpoint, "PATCH", data, headers, config);
  };

  const deleteRequest = async (
    endpoint: string,
    headers?: Record<string, string>,
    config?: RequestConfig
  ): Promise<Response> => {
    return handleRequest(endpoint, "DELETE", undefined, headers, config);
  };

  return {
    getRequest,
    postRequest,
    putRequest,
    patchRequest,
    deleteRequest,
    isLoading,
    error,
  };
};

export default useApi;
